import React, { useState, useEffect } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0c0d12', paper: '#14171f' },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: { main: '#6aa9ff' },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const tableHeadCellSx = {
  fontWeight: 700,
  color: '#d9e6ff',
  backgroundColor: 'rgba(30, 35, 52, 0.92)',
  borderBottom: '1px solid rgba(255,255,255,0.12)',
  textAlign: 'center',
};

const DataTable = () => {
  const [smsSnapshot, setSmsSnapshot] = useState({
    snapshot_sms_earnings: { g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 },
    snapshot_total_sent_sms: { g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 },
    snapshot_sms_billed_segments: { g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 },
  });

  const [earningsSnapshot, setEarningsSnapshot] = useState({
    g1: { duration: '00:00:00', earnings: 0 },
    g2: { duration: '00:00:00', earnings: 0 },
    g3: { duration: '00:00:00', earnings: 0 },
    g4: { duration: '00:00:00', earnings: 0 },
    g5: { duration: '00:00:00', earnings: 0 },
    g6: { duration: '00:00:00', earnings: 0 },
    g7: { duration: '00:00:00', earnings: 0 },
    g8: { duration: '00:00:00', earnings: 0 },
    g9: { duration: '00:00:00', earnings: 0 },
  });

  const retrieveData = async () => {
    try {
      const responseSMS = await fetch('https://frek.cc/proxy/38.54.86.24:5006/snapshot/sms');
      const dataSMS = await responseSMS.json();
      setSmsSnapshot(dataSMS);

      const responseEarnings = await fetch('https://frek.cc/proxy/38.54.86.24:5006/snapshot/earnings');
      const dataEarnings = await responseEarnings.json();
      setEarningsSnapshot(dataEarnings);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    document.title = 'Earnings';
  }, []);

  useEffect(() => {
    retrieveData();
    const interval = setInterval(retrieveData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const now = new Date();
    const msToMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0) - now;
    const timer = setTimeout(() => {
      retrieveData();
      setInterval(retrieveData, 24 * 60 * 60 * 1000);
    }, msToMidnight);

    return () => clearTimeout(timer);
  }, []);

  const totalVoipDuration = (() => {
    const totalSeconds = Object.values(earningsSnapshot).reduce((acc, data) => {
      const parts = data.duration.split(':').map((part) => parseInt(part, 10));
      return acc + (parts[0] * 3600) + (parts[1] * 60) + parts[2];
    }, 0);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  })();

  const totalVoipBill = Object.values(earningsSnapshot).reduce((acc, data) => acc + data.earnings, 0);
  const totalSmsSent = Object.values(smsSnapshot.snapshot_total_sent_sms).reduce((acc, count) => acc + count, 0);
  const totalSmsBill = Object.values(smsSnapshot.snapshot_sms_earnings).reduce((acc, value) => acc + value, 0);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  Daily Snapshot
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`VOIP Duration ${totalVoipDuration}`} sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }} />
                  <Chip label={`VOIP Bill USD ${totalVoipBill.toFixed(3)}`} sx={{ bgcolor: 'rgba(90,210,122,0.18)', color: '#b9f2c8', fontWeight: 700 }} />
                  <Chip label={`SMS Sent ${totalSmsSent.toLocaleString()}`} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#e8e9ee', fontWeight: 700 }} />
                  <Chip label={`SMS Bill EUR ${totalSmsBill.toFixed(3)}`} sx={{ bgcolor: 'rgba(255,184,77,0.2)', color: '#ffd9a0', fontWeight: 700 }} />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Today VOIP Data</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Gateway</TableCell>
                        <TableCell sx={tableHeadCellSx}>Duration</TableCell>
                        <TableCell sx={tableHeadCellSx}>Bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(earningsSnapshot).map(([gateway, data]) => (
                        <TableRow key={gateway} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell align="center">{gateway}</TableCell>
                          <TableCell align="center">{data.duration}</TableCell>
                          <TableCell align="center">USD {data.earnings.toFixed(3)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)', fontWeight: 700 } }}>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">{totalVoipDuration}</TableCell>
                        <TableCell align="center">USD {totalVoipBill.toFixed(3)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Today SMS Data</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Goip</TableCell>
                        <TableCell sx={tableHeadCellSx}>Sent</TableCell>
                        <TableCell sx={tableHeadCellSx}>Bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(smsSnapshot.snapshot_total_sent_sms).map(([gateway, count]) => (
                        <TableRow key={gateway} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell align="center">{gateway}</TableCell>
                          <TableCell align="center">{count.toLocaleString()}</TableCell>
                          <TableCell align="center">EUR {smsSnapshot.snapshot_sms_earnings[gateway].toFixed(3)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)', fontWeight: 700 } }}>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">{totalSmsSent.toLocaleString()}</TableCell>
                        <TableCell align="center">EUR {totalSmsBill.toFixed(3)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DataTable;
